/*
  DuplicateClientsDialog — one-tap merge of duplicate client records.

  Detection is driven by GET /api/clients/duplicates. Each pair
  proposes a survivor (target) and a source; the reviewer can swap
  the direction, dry-run the merge to see counts per collection, and
  then apply the merge in one call.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, GitMerge, RefreshCw, ArrowRight, ArrowLeftRight, AlertTriangle,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";


const CONF_TONE = {
  high: "bg-emerald-50 text-emerald-800 border-emerald-300",
  medium: "bg-amber-50 text-amber-800 border-amber-300",
  low: "bg-slate-50 text-slate-700 border-slate-300",
};

const SIGNAL_LABEL = {
  phone: "Phone match",
  "name+synthetic": "No-email stub",
  name: "Name match",
};


export default function DuplicateClientsDialog({ open, onOpenChange, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [pairs, setPairs] = useState([]);
  const [direction, setDirection] = useState({});     // pairId -> "forward" | "reverse"
  const [preview, setPreview] = useState({});         // pairId -> {counts, total_moved}
  const [busy, setBusy] = useState({});               // pairId -> "preview" | "merge"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients/duplicates");
      setPairs(r.data?.pairs || []);
      setPreview({});
    } catch (e) {
      toast.error("Couldn't load duplicates", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  function pairId(p) {
    return `${p.target.email}|${p.source.email}`.toLowerCase();
  }

  function currentEnds(p) {
    const dir = direction[pairId(p)] || "forward";
    return dir === "forward"
      ? { target: p.target, source: p.source }
      : { target: p.source, source: p.target };
  }

  async function runPreview(p) {
    const id = pairId(p);
    const { target, source } = currentEnds(p);
    setBusy((b) => ({ ...b, [id]: "preview" }));
    try {
      const r = await api.post("/clients/merge", {
        target_email: target.email, source_email: source.email,
        dry_run: true,
      });
      setPreview((prev) => ({ ...prev, [id]: r.data }));
    } catch (e) {
      toast.error("Preview failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setBusy((b) => ({ ...b, [id]: undefined })); }
  }

  async function runMerge(p) {
    const id = pairId(p);
    const { target, source } = currentEnds(p);
    if (!window.confirm(
      `Merge ${source.email} INTO ${target.email}?\n\n` +
      `The source record will be deleted and every program, note,\n` +
      `medical record and outcome measure will be re-pointed at\n` +
      `the survivor. This cannot be undone from the UI.`,
    )) return;
    setBusy((b) => ({ ...b, [id]: "merge" }));
    try {
      const r = await api.post("/clients/merge", {
        target_email: target.email, source_email: source.email,
      });
      toast.success(`Merged — moved ${r.data.total_moved} record(s)`);
      // Drop this pair from the list; refresh so any cascading dups
      // that resolved automatically also drop off.
      setPairs((ps) => ps.filter((x) => pairId(x) !== id));
      onMerged?.();
      // Re-fetch after a short delay so the backend has time to
      // settle (rules-engine recomputes, etc.)
      setTimeout(load, 250);
    } catch (e) {
      toast.error("Merge failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setBusy((b) => ({ ...b, [id]: undefined })); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="duplicate-clients-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-4 w-4 text-indigo-700" /> Duplicate clients
            {pairs.length > 0 && (
              <Badge className="bg-amber-50 text-amber-800 border-amber-300 text-2xs">
                {pairs.length} pair{pairs.length === 1 ? "" : "s"}
              </Badge>
            )}
            <div className="flex-1" />
            <Button
              size="sm" variant="ghost" onClick={load}
              disabled={loading}
              data-testid="duplicates-refresh"
            >
              {loading
                ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Refresh
            </Button>
          </DialogTitle>
          <DialogDescription>
            Same person listed twice? Merge moves every program, note,
            medical record and outcome measure from the source onto the
            surviving record in one tap. Appointments follow the email
            automatically from Acuity.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-2" data-testid="duplicates-list">
          {loading ? (
            <div className="text-sm text-zinc-500 flex items-center gap-2 py-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Scanning…
            </div>
          ) : pairs.length === 0 ? (
            <div className="text-sm text-zinc-600 flex items-center gap-2 py-6"
                  data-testid="duplicates-empty">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              No duplicates detected. Nothing to merge.
            </div>
          ) : (
            pairs.map((p) => {
              const id = pairId(p);
              const { target, source } = currentEnds(p);
              const prev = preview[id];
              const b = busy[id];
              return (
                <div
                  key={id}
                  className="border border-zinc-200 rounded-md p-3 bg-white"
                  data-testid={`duplicate-pair-${id.replace(/[^a-z0-9]/g, "-")}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={cn("text-2xs uppercase", CONF_TONE[p.confidence] || CONF_TONE.low)}>
                      {p.confidence}
                    </Badge>
                    {p.signals.map((s) => (
                      <Badge
                        key={s}
                        className="text-2xs bg-slate-100 text-slate-700 border-slate-300"
                      >
                        {SIGNAL_LABEL[s] || s}
                      </Badge>
                    ))}
                    <div className="flex-1" />
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => setDirection((d) => ({
                        ...d,
                        [id]: (d[id] || "forward") === "forward" ? "reverse" : "forward",
                      }))}
                      className="h-7 text-xs"
                      title="Swap survivor and source"
                      data-testid={`swap-${id.replace(/[^a-z0-9]/g, "-")}`}
                    >
                      <ArrowLeftRight className="h-3 w-3 mr-1" /> Swap direction
                    </Button>
                  </div>

                  <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                    <ClientCard label="Survivor" client={target} tone="emerald" />
                    <ArrowRight className="h-5 w-5 text-zinc-400" />
                    <ClientCard label="Source (deleted)" client={source} tone="red" />
                  </div>

                  {prev && (
                    <div className="mt-2 text-2xs text-zinc-700 border border-indigo-200 bg-indigo-50/60 rounded p-2"
                          data-testid={`preview-${id.replace(/[^a-z0-9]/g, "-")}`}>
                      Moving <b>{prev.total_moved}</b> record(s):
                      <div className="mt-1 flex flex-wrap gap-1">
                        {Object.entries(prev.counts || {})
                          .filter(([, n]) => n > 0)
                          .sort((a, b) => b[1] - a[1])
                          .map(([coll, n]) => (
                            <span
                              key={coll}
                              className="px-1.5 py-0.5 bg-white border border-indigo-200 rounded text-2xs font-mono"
                            >
                              {coll} · {n}
                            </span>
                          ))}
                        {Object.values(prev.counts || {}).every((n) => !n) && (
                          <span className="italic text-zinc-500">
                            No child records — source only had a directory entry.
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => runPreview(p)}
                      disabled={!!b}
                      data-testid={`preview-btn-${id.replace(/[^a-z0-9]/g, "-")}`}
                    >
                      {b === "preview"
                        ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Previewing…</>
                        : "Preview"}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => runMerge(p)}
                      disabled={!!b}
                      data-testid={`merge-btn-${id.replace(/[^a-z0-9]/g, "-")}`}
                    >
                      {b === "merge"
                        ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Merging…</>
                        : <><GitMerge className="h-3.5 w-3.5 mr-1" /> Merge</>}
                    </Button>
                    {p.confidence !== "high" && (
                      <span className="text-2xs text-amber-800 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Review the two rows carefully before merging.
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ClientCard({ label, client, tone = "emerald" }) {
  const toneCls = {
    emerald: "border-emerald-200 bg-emerald-50/40",
    red: "border-red-200 bg-red-50/40",
  }[tone];
  const labelCls = {
    emerald: "text-emerald-800",
    red: "text-red-800",
  }[tone];
  return (
    <div className={cn("border rounded p-2", toneCls)}>
      <div className={cn("text-2xs uppercase tracking-wide font-semibold mb-1", labelCls)}>
        {label}
      </div>
      <div className="text-sm font-medium truncate" title={client.name}>
        {client.name || "(no name)"}
      </div>
      <div className="text-2xs text-zinc-700 truncate" title={client.email}>
        {client.email}
      </div>
      <div className="text-2xs text-zinc-500 flex items-center gap-2 mt-1">
        {client.phone && <span>📞 {client.phone}</span>}
        <span className="uppercase">{client.source}</span>
        {client.is_synthetic && (
          <Badge className="text-2xs bg-amber-100 text-amber-800 border-amber-300">
            no email
          </Badge>
        )}
        {client.appointment_count > 0 && (
          <span>· {client.appointment_count} appt</span>
        )}
      </div>
    </div>
  );
}
